/**
 * Cálculos de comida (design.md, 8.11; Req. 18 a 20).
 *
 * Código puro. Reglas que importan:
 * - Los valores se guardan con precisión completa y se redondean una sola vez,
 *   al presentar (numbers.ts).
 * - Un valor desconocido es null y sigue null al multiplicar: nunca se toma
 *   como cero. Los totales cuentan aparte cuántos registros no lo tienen, para
 *   decir "datos incompletos" en lugar de presentar un total como completo.
 * - Cada registro guarda su copia de valores (Req. 18.4): aquí se calcula esa
 *   copia; editar después el alimento no cambia lo ya registrado.
 */

import type {
  CalidadDato,
  Dish,
  EstadoCoccion,
  Food,
  MealEntry,
  Nutrientes,
  Porcion,
  TiempoComida,
} from '@/db/types';
import { formatearNumero } from './numbers';

// ---------------------------------------------------------------------------
// Base de alimentos incluida (design.md, 7.6)
// ---------------------------------------------------------------------------

export interface AlimentoBase {
  /** 'ab_tortilla_maiz' */
  id: string;
  nombre: string;
  sinonimos: string[];
  categoria: string;
  /** Si los valores son del alimento crudo o cocido; null = no aplica. */
  estado: EstadoCoccion | null;
  por100g: Nutrientes;
  porciones: Porcion[];
  rompeAyuno: boolean;
  fuente: { fdcId: number; tipo: 'FNDDS' | 'SR Legacy' | 'Foundation'; descripcion: string };
}

/** Req. 18.11 */
export const LIMITE_KCAL = 10_000;
export const LIMITE_GRAMOS = 5_000;

/** Clave de la "porción" en gramos. */
export const GRAMOS = 'g';

/**
 * Clave de la "porción" en mililitros (0.3.5). Existe cuando la porción de
 * la etiqueta es en ml, como "100 ml": así un líquido se registra en ml sin
 * suponer que 1 ml pesa 1 g.
 */
export const MILILITROS = 'ml';

/** Los ml de una porción como "100 ml" o "250 ml"; null si no es en ml. */
export function mlDePorcion(p: Porcion): number | null {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*ml\s*$/i.exec(p.nombre);
  if (m === null || m[1] === undefined) return null;
  const ml = Number(m[1].replace(',', '.'));
  return Number.isFinite(ml) && ml > 0 ? ml : null;
}

// ---------------------------------------------------------------------------
// Porciones y factor (design.md, 8.11)
// ---------------------------------------------------------------------------

/** Lo necesario para calcular: la referencia, sus nutrientes y las porciones. */
export interface Calculable {
  referencia: Food['referencia'];
  /** Por la referencia: por 100 g o por la porción de referencia. */
  nutrientes: Nutrientes;
  porciones: readonly Porcion[];
}

export function calculableDeBase(alimento: AlimentoBase): Calculable {
  return { referencia: { tipo: 'por100g' }, nutrientes: alimento.por100g, porciones: alimento.porciones };
}

export function calculableDePropio(alimento: Pick<Food, 'referencia' | 'nutrientes' | 'porciones'>): Calculable {
  return {
    referencia: alimento.referencia,
    nutrientes: alimento.nutrientes,
    porciones: alimento.porciones,
  };
}

function dividir(n: Nutrientes, divisor: number): Nutrientes {
  return multiplicar(n, 1 / divisor);
}

/**
 * Para editar un registro sin depender del alimento de origen (Req. 18.4): la
 * referencia sale de la copia guardada en el propio registro. Con gramos
 * conocidos se puede pasar a gramos o a otra porción con gramos; sin gramos,
 * solo cambiar la cantidad de la misma porción. null en un registro rápido,
 * que se edita escribiendo sus valores.
 */
export function calculableDeRegistro(
  registro: Pick<MealEntry, 'origen' | 'cantidad' | 'porcion' | 'gramos' | 'valores'>,
  porcionesDelOrigen: readonly Porcion[] = [],
): Calculable | null {
  if (registro.origen.tipo === 'rapido' || !(registro.cantidad > 0)) return null;
  const { porcion, gramos, cantidad, valores } = registro;
  if (porcion === GRAMOS || porcion === null) {
    if (gramos === null || !(gramos > 0)) return null;
    return {
      referencia: { tipo: 'por100g' },
      nutrientes: dividir(valores, gramos / 100),
      porciones: porcionesDelOrigen,
    };
  }
  const gramosPorUnidad = gramos !== null && gramos > 0 ? gramos / cantidad : null;
  return {
    referencia: { tipo: 'porPorcion', porcion: { nombre: porcion, gramos: gramosPorUnidad } },
    nutrientes: dividir(valores, cantidad),
    porciones: porcionesDelOrigen,
  };
}

export interface OpcionPorcion {
  /** Nombre de la porción o 'g'. Es lo que se guarda en `MealEntry.porcion`. */
  clave: string;
  /** "pieza (30 g)", "gramos" */
  etiqueta: string;
  gramos: number | null;
}

function etiquetaPorcion(p: Porcion): string {
  return p.gramos === null ? p.nombre : `${p.nombre} (${formatearNumero(p.gramos, { decimales: 1, fijos: false })} g)`;
}

/** Las porciones que se pueden elegir para un alimento, sin repetidas. */
export function opcionesDePorcion(c: Calculable): OpcionPorcion[] {
  const opciones: OpcionPorcion[] = [];
  const agregar = (o: OpcionPorcion) => {
    if (!opciones.some((x) => x.clave === o.clave)) opciones.push(o);
  };
  const conGramos = c.porciones.filter((p) => p.gramos !== null && p.gramos > 0);

  if (c.referencia.tipo === 'por100g') {
    for (const p of conGramos) agregar({ clave: p.nombre, etiqueta: etiquetaPorcion(p), gramos: p.gramos });
    agregar({ clave: GRAMOS, etiqueta: 'gramos', gramos: 1 });
    return opciones;
  }

  const ref = c.referencia.porcion;
  agregar({ clave: ref.nombre, etiqueta: etiquetaPorcion(ref), gramos: ref.gramos });
  const ml = mlDePorcion(ref);
  if (ml !== null) {
    agregar({ clave: MILILITROS, etiqueta: 'mililitros', gramos: ref.gramos !== null && ref.gramos > 0 ? ref.gramos / ml : null });
  }
  if (ref.gramos !== null && ref.gramos > 0) {
    for (const p of conGramos) agregar({ clave: p.nombre, etiqueta: etiquetaPorcion(p), gramos: p.gramos });
    agregar({ clave: GRAMOS, etiqueta: 'gramos', gramos: 1 });
  }
  return opciones;
}

export type ResultadoFactor =
  | { ok: true; factor: number; gramos: number | null }
  | { ok: false; error: string };

/** El factor por el que se multiplican los nutrientes de la referencia (design.md, 8.11). */
export function factor(c: Calculable, cantidad: number, porcion: string): ResultadoFactor {
  if (!Number.isFinite(cantidad) || cantidad <= 0) {
    return { ok: false, error: 'La cantidad debe ser mayor que cero.' };
  }
  const sinGramos = { ok: false as const, error: 'Esta porción no tiene gramos: elige otra.' };

  if (c.referencia.tipo === 'por100g') {
    if (porcion === GRAMOS) return { ok: true, factor: cantidad / 100, gramos: cantidad };
    const p = c.porciones.find((x) => x.nombre === porcion);
    if (p === undefined) return { ok: false, error: 'Elige una porción.' };
    if (p.gramos === null || !(p.gramos > 0)) return sinGramos;
    const gramos = cantidad * p.gramos;
    return { ok: true, factor: gramos / 100, gramos };
  }

  const ref = c.referencia.porcion;
  const gramosRef = ref.gramos !== null && ref.gramos > 0 ? ref.gramos : null;
  if (porcion === ref.nombre) {
    return { ok: true, factor: cantidad, gramos: gramosRef === null ? null : cantidad * gramosRef };
  }
  // En ml, si la porción de la etiqueta es en ml: sin suponer cuánto pesa
  const mlRef = mlDePorcion(ref);
  if (porcion === MILILITROS && mlRef !== null) {
    return { ok: true, factor: cantidad / mlRef, gramos: gramosRef === null ? null : (cantidad / mlRef) * gramosRef };
  }
  if (gramosRef === null) return sinGramos;
  if (porcion === GRAMOS) return { ok: true, factor: cantidad / gramosRef, gramos: cantidad };
  const p = c.porciones.find((x) => x.nombre === porcion);
  if (p === undefined) return { ok: false, error: 'Elige una porción.' };
  if (p.gramos === null || !(p.gramos > 0)) return sinGramos;
  return { ok: true, factor: (cantidad * p.gramos) / gramosRef, gramos: cantidad * p.gramos };
}

/** Los null siguen null: un dato desconocido no se vuelve cero. */
export function multiplicar(n: Nutrientes, f: number): Nutrientes {
  const por = (v: number | null) => (v === null ? null : v * f);
  return {
    kcal: n.kcal * f,
    proteinaG: por(n.proteinaG),
    carbohidratosG: por(n.carbohidratosG),
    grasaG: por(n.grasaG),
    fibraG: por(n.fibraG),
  };
}

export type ResultadoCalculo =
  | { ok: true; valores: Nutrientes; gramos: number | null }
  | { ok: false; error: string };

export function calcular(c: Calculable, cantidad: number, porcion: string): ResultadoCalculo {
  const f = factor(c, cantidad, porcion);
  if (!f.ok) return f;
  return { ok: true, valores: multiplicar(c.nutrientes, f.factor), gramos: f.gramos };
}

// ---------------------------------------------------------------------------
// Validación de un registro (Req. 18.11)
// ---------------------------------------------------------------------------

const esValorValido = (v: number | null) => v === null || (Number.isFinite(v) && v >= 0);

/** null si se puede guardar; si no, el motivo para mostrarlo junto al botón. */
export function validarRegistro(r: {
  nombre: string;
  cantidad: number;
  gramos: number | null;
  valores: Nutrientes;
}): string | null {
  if (r.nombre.trim() === '') return 'Escribe el nombre.';
  if (!Number.isFinite(r.cantidad) || r.cantidad <= 0) return 'La cantidad debe ser mayor que cero.';
  const { kcal, proteinaG, carbohidratosG, grasaG, fibraG } = r.valores;
  if (![kcal, proteinaG, carbohidratosG, grasaG, fibraG].every(esValorValido)) {
    return 'Los valores no pueden ser negativos.';
  }
  if (r.gramos !== null && r.gramos > LIMITE_GRAMOS) {
    return `Un registro no puede pasar de ${formatearNumero(LIMITE_GRAMOS, { decimales: 0 })} g.`;
  }
  if (kcal > LIMITE_KCAL) {
    return `Un registro no puede pasar de ${formatearNumero(LIMITE_KCAL, { decimales: 0 })} kcal.`;
  }
  return null;
}

/**
 * Revisión de una etiqueta: si 4 × proteína + 4 × carbohidratos + 9 × grasa
 * se aleja más de 15 % de las kcal, probablemente hay un número mal escrito.
 * Solo avisa: hay etiquetas que redondean mucho. null si no hay qué decir.
 */
export function revisarEtiqueta(n: Nutrientes): string | null {
  const { kcal, proteinaG, carbohidratosG, grasaG } = n;
  if (proteinaG === null || carbohidratosG === null || grasaG === null || kcal < 20) return null;
  const fibra = Math.min(n.fibraG ?? 0, carbohidratosG);
  const porMacros = 4 * proteinaG + 4 * (carbohidratosG - fibra) + 2 * fibra + 9 * grasaG;
  if (Math.abs(porMacros - kcal) / kcal <= 0.15) return null;
  return `Con esos macros saldrían unas ${formatearNumero(porMacros, { decimales: 0 })} kcal. Revisa que estén bien escritos.`;
}

// ---------------------------------------------------------------------------
// Totales del día y de cada comida
// ---------------------------------------------------------------------------

export interface Totales {
  kcal: number;
  proteinaG: number;
  carbohidratosG: number;
  grasaG: number;
  fibraG: number;
  registros: number;
  /** Cuántos registros no traen cada dato: el total de ese dato está incompleto. */
  sinDato: { proteinaG: number; carbohidratosG: number; grasaG: number; fibraG: number };
}

export function sumar(entradas: readonly Pick<MealEntry, 'valores'>[]): Totales {
  const t: Totales = {
    kcal: 0,
    proteinaG: 0,
    carbohidratosG: 0,
    grasaG: 0,
    fibraG: 0,
    registros: entradas.length,
    sinDato: { proteinaG: 0, carbohidratosG: 0, grasaG: 0, fibraG: 0 },
  };
  for (const { valores } of entradas) {
    t.kcal += Number.isFinite(valores.kcal) ? valores.kcal : 0;
    for (const campo of ['proteinaG', 'carbohidratosG', 'grasaG', 'fibraG'] as const) {
      const v = valores[campo];
      if (v === null || !Number.isFinite(v)) t.sinDato[campo] += 1;
      else t[campo] += v;
    }
  }
  return t;
}

/** Registros a los que les falta algún macro (proteína, carbohidratos o grasa). */
export function registrosIncompletos(entradas: readonly Pick<MealEntry, 'valores'>[]): number {
  return entradas.filter((e) => macrosIncompletos(e.valores)).length;
}

export function macrosIncompletos(v: Nutrientes): boolean {
  return v.proteinaG === null || v.carbohidratosG === null || v.grasaG === null;
}

export const TIEMPOS: readonly TiempoComida[] = ['desayuno', 'comida', 'cena', 'colacion'];

export const NOMBRE_TIEMPO: Readonly<Record<TiempoComida, string>> = {
  desayuno: 'Desayuno',
  comida: 'Comida',
  cena: 'Cena',
  colacion: 'Colación',
};

export interface GrupoTiempo {
  tiempo: TiempoComida;
  etiqueta: string;
  entradas: MealEntry[];
  totales: Totales;
}

/** Los cuatro tiempos, siempre en orden y aunque estén vacíos (Req. 18.5). */
export function agruparPorTiempo(entradas: readonly MealEntry[]): GrupoTiempo[] {
  const ordenadas = [...entradas].sort(
    (a, b) => a.momento.localeCompare(b.momento) || a.creadoEn.localeCompare(b.creadoEn),
  );
  return TIEMPOS.map((tiempo) => {
    const delTiempo = ordenadas.filter((e) => e.tiempo === tiempo);
    return { tiempo, etiqueta: NOMBRE_TIEMPO[tiempo], entradas: delTiempo, totales: sumar(delTiempo) };
  });
}

/** Tiempo de comida que corresponde a una hora, para proponerlo al agregar. */
export function tiempoSegunHora(hora: string): TiempoComida {
  const h = Number(hora.slice(0, 2));
  if (h >= 5 && h < 12) return 'desayuno';
  if (h >= 12 && h < 18) return 'comida';
  if (h >= 18 && h < 23) return 'cena';
  return 'colacion';
}

// ---------------------------------------------------------------------------
// Avance contra la meta (Req. 18.5, 18.10)
// ---------------------------------------------------------------------------

export interface Avance {
  meta: number | null;
  consumido: number;
  /** Lo que falta para llegar; null sin meta o si ya se llegó. */
  faltan: number | null;
  /** Cuánto pasa de la meta; null sin meta o si no se ha pasado. */
  exceso: number | null;
  /** 0 a 1 para la barra; null sin meta. */
  proporcion: number | null;
}

export function avance(consumido: number, meta: number | null): Avance {
  if (meta === null || !(meta > 0)) {
    return { meta: null, consumido, faltan: null, exceso: null, proporcion: null };
  }
  const diferencia = meta - consumido;
  return {
    meta,
    consumido,
    faltan: diferencia > 0 ? diferencia : null,
    exceso: diferencia < 0 ? -diferencia : null,
    proporcion: Math.min(1, Math.max(0, consumido / meta)),
  };
}

// ---------------------------------------------------------------------------
// Presentación
// ---------------------------------------------------------------------------

/** "1,230 kcal" */
export function formatearKcal(kcal: number): string {
  return `${formatearNumero(kcal, { decimales: 0 })} kcal`;
}

/** "13.4 g", "140 g" */
export function formatearGramos(gramos: number): string {
  return `${formatearNumero(gramos, { decimales: 1, fijos: false })} g`;
}

/** "P 1.7 g · C 13.4 g · G 0.9 g"; lo que falta dice "sin dato". */
export function textoMacros(v: Nutrientes): string {
  if (v.proteinaG === null && v.carbohidratosG === null && v.grasaG === null) return 'Sin macros';
  const parte = (letra: string, g: number | null) => `${letra} ${g === null ? 'sin dato' : formatearGramos(g)}`;
  return [parte('P', v.proteinaG), parte('C', v.carbohidratosG), parte('G', v.grasaG)].join(' · ');
}

/** "65 kcal · P 1.7 g · C 13.4 g · G 0.9 g · fibra 1.9 g" */
export function textoValores(v: Nutrientes): string {
  const partes = [formatearKcal(v.kcal), textoMacros(v)];
  if (v.fibraG !== null) partes.push(`fibra ${formatearGramos(v.fibraG)}`);
  return partes.join(' · ');
}

const formatearCantidad = (n: number) => formatearNumero(n, { decimales: 2, fijos: false });

/**
 * "1 pieza (30 g)", "2 × pieza (60 g)", "1 cucharadita (4.5 g)", "150 g",
 * "200 ml". null en un registro rápido. Con "×" no hace falta adivinar el
 * plural de cada porción.
 */
export function textoCantidad(r: Pick<MealEntry, 'cantidad' | 'porcion' | 'gramos'>): string | null {
  if (r.porcion === null) return null;
  if (r.porcion === GRAMOS) return `${formatearCantidad(r.cantidad)} g`;
  if (r.porcion === MILILITROS) return `${formatearCantidad(r.cantidad)} ml`;
  const gramos = r.gramos === null ? '' : ` (${formatearNumero(r.gramos, { decimales: 1, fijos: false })} g)`;
  const cantidad = r.cantidad === 1 ? '1 ' : `${formatearCantidad(r.cantidad)} × `;
  return `${cantidad}${r.porcion}${gramos}`;
}

// ---------------------------------------------------------------------------
// Repetir un registro desde Recientes
// ---------------------------------------------------------------------------

/**
 * El alimento de origen como está hoy, si todavía existe. Un platillo trae su
 * cálculo con los ingredientes de hoy (domain/platillos.ts).
 */
export type OrigenActual =
  | { tipo: 'base'; alimento: AlimentoBase }
  | { tipo: 'propio'; alimento: Food }
  | { tipo: 'platillo'; platillo: Dish; calculable: Calculable; rompeAyuno: boolean }
  | null;

/** Lo que se necesita del origen para repetir: valores, nombre, ayuno y estado. */
function datosDeOrigen(o: Exclude<OrigenActual, null>) {
  if (o.tipo === 'platillo') {
    return { calculable: o.calculable, nombre: o.platillo.nombre, rompeAyuno: o.rompeAyuno, estado: null, id: o.platillo.id };
  }
  const calculable = o.tipo === 'base' ? calculableDeBase(o.alimento) : calculableDePropio(o.alimento);
  const { alimento } = o;
  return { calculable, nombre: alimento.nombre, rompeAyuno: alimento.rompeAyuno, estado: estadoDe(alimento), id: alimento.id };
}

export interface Repeticion {
  calculable: Calculable;
  nombre: string;
  rompeAyuno: boolean;
  estado: EstadoCoccion | null;
  origen: MealEntry['origen'];
  cantidad: number;
  porcion: string;
  /** true: con los valores actuales del alimento; false: con la copia del registro. */
  actual: boolean;
}

/**
 * Un registro nuevo igual a uno anterior. Si el alimento todavía existe, se
 * calcula con sus valores ACTUALES (por ejemplo, después de corregir su
 * etiqueta) y la misma cantidad; el registro anterior no cambia (Req. 18.4).
 * Solo si el alimento ya no existe se usa la copia del registro.
 */
export function repetirRegistro(previo: MealEntry, origenActual: OrigenActual): Repeticion | null {
  if (origenActual !== null) {
    const actual = datosDeOrigen(origenActual);
    const { calculable } = actual;
    const opciones = opcionesDePorcion(calculable);
    let porcion: string;
    let cantidad: number;
    if (previo.porcion !== null && opciones.some((o) => o.clave === previo.porcion)) {
      porcion = previo.porcion;
      cantidad = previo.cantidad;
    } else if (previo.gramos !== null && opciones.some((o) => o.clave === GRAMOS)) {
      // La porción de antes ya no existe: los mismos gramos
      porcion = GRAMOS;
      cantidad = previo.gramos;
    } else {
      porcion = opciones[0]?.clave ?? GRAMOS;
      cantidad = porcion === GRAMOS ? 100 : 1;
    }
    return {
      calculable,
      nombre: actual.nombre,
      rompeAyuno: actual.rompeAyuno,
      estado: actual.estado,
      origen: { tipo: origenActual.tipo, refId: actual.id },
      cantidad,
      porcion,
      actual: true,
    };
  }
  const calculable = calculableDeRegistro(previo);
  if (calculable === null) return null;
  return {
    calculable,
    nombre: previo.nombre,
    rompeAyuno: previo.rompeAyuno,
    estado: estadoDe(previo),
    origen: previo.origen,
    cantidad: previo.cantidad,
    porcion: previo.porcion ?? GRAMOS,
    actual: false,
  };
}

// ---------------------------------------------------------------------------
// Calidad del dato de un alimento propio (0.3.5)
// ---------------------------------------------------------------------------

export const CALIDADES: readonly CalidadDato[] = ['etiqueta', 'referencia', 'aproximada', 'estimada'];

export const ETIQUETAS_CALIDAD: Readonly<Record<CalidadDato, string>> = {
  etiqueta: 'Etiqueta',
  referencia: 'Referencia',
  aproximada: 'Aproximación',
  estimada: 'Estimación',
};

export const NOTAS_CALIDAD: Readonly<Record<CalidadDato, string>> = {
  etiqueta: 'De la etiqueta de tu producto.',
  referencia: 'Referencia genérica, no la etiqueta de tu producto.',
  aproximada: 'Aproximación con la etiqueta de otra presentación; puede variar.',
  estimada: 'Estimación: no es una medición ni la etiqueta de tu producto.',
};

/** Normaliza el campo opcional: ausente o raro = null (como se capturó). */
export function calidadDe(x: { calidad?: unknown }): CalidadDato | null {
  return CALIDADES.includes(x.calidad as CalidadDato) ? (x.calidad as CalidadDato) : null;
}

/** La que conviene ver en la lista del día: aproximación o estimación; las demás no se marcan. */
export function calidadParaAvisar(x: { calidad?: unknown }): CalidadDato | null {
  const c = calidadDe(x);
  return c === 'aproximada' || c === 'estimada' ? c : null;
}

export const ETIQUETA_ESTADO: Readonly<Record<EstadoCoccion, string>> = {
  crudo: 'Crudo',
  cocido: 'Cocido',
};

/** Normaliza el campo opcional del esquema 2: ausente se lee como null. */
export function estadoDe(registro: { estado?: EstadoCoccion | null }): EstadoCoccion | null {
  return registro.estado === 'crudo' || registro.estado === 'cocido' ? registro.estado : null;
}
